import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { 
  Mail, MessageCircle, Plus, Settings, CheckCircle, XCircle, 
  Trash2, TestTube, Eye, EyeOff, AlertCircle, CreditCard, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProvidersPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payment');
  const [providers, setProviders] = useState([]);
  const [availableProviders, setAvailableProviders] = useState({ email_providers: [], whatsapp_providers: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  // Payment Gateway State
  const [gatewaySettings, setGatewaySettings] = useState(null);
  const [gatewayDialogOpen, setGatewayDialogOpen] = useState(false);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [showGatewaySecret, setShowGatewaySecret] = useState(false);
  const [gatewayForm, setGatewayForm] = useState({
    key_id: '',
    key_secret: '',
    merchant_name: 'Cloud Campus'
  });
  
  const [newProvider, setNewProvider] = useState({
    provider_type: 'email',
    provider_name: '',
    is_default: false,
    config: {}
  });

  useEffect(() => {
    fetchData();
    fetchGatewaySettings();
  }, []);

  const fetchGatewaySettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/global-gateway-settings`, {
        headers: getAuthHeader()
      });
      setGatewaySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch gateway settings:', error);
    }
  };

  const saveGatewaySettings = async () => {
    if (!gatewayForm.key_id || !gatewayForm.key_secret) {
      toast.error('Please enter both API Key ID and Secret');
      return;
    }
    
    setGatewayLoading(true);
    try {
      await axios.post(`${API_URL}/api/payments/global-gateway-settings`, gatewayForm, {
        headers: getAuthHeader()
      });
      toast.success('Payment gateway configured successfully!');
      setGatewayDialogOpen(false);
      setGatewayForm({ key_id: '', key_secret: '', merchant_name: 'Cloud Campus' });
      fetchGatewaySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure gateway');
    } finally {
      setGatewayLoading(false);
    }
  };

  const testGateway = async () => {
    setGatewayLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/test-gateway`, {}, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to test gateway');
    } finally {
      setGatewayLoading(false);
    }
  };

  const removeGateway = async () => {
    if (!window.confirm('Are you sure you want to remove the payment gateway configuration?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/payments/global-gateway-settings`, {
        headers: getAuthHeader()
      });
      toast.success('Gateway configuration removed');
      fetchGatewaySettings();
    } catch (error) {
      toast.error('Failed to remove gateway');
    }
  };

  const fetchData = async () => {
    try {
      const [providersRes, availableRes] = await Promise.all([
        axios.get(`${API_URL}/api/super-admin/providers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/super-admin/available-providers`, { headers: getAuthHeader() })
      ]);
      setProviders(providersRes.data);
      setAvailableProviders(availableRes.data);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProvider = async () => {
    try {
      await axios.post(`${API_URL}/api/super-admin/providers`, newProvider, { headers: getAuthHeader() });
      toast.success('Provider configured successfully');
      setDialogOpen(false);
      setNewProvider({ provider_type: 'email', provider_name: '', is_default: false, config: {} });
      fetchData();
    } catch (error) {
      toast.error('Failed to configure provider');
    }
  };

  const handleTestProvider = async (providerId) => {
    try {
      const result = await axios.post(`${API_URL}/api/super-admin/providers/${providerId}/test`, {}, { headers: getAuthHeader() });
      if (result.data.success) {
        toast.success(result.data.message);
      } else {
        toast.error(result.data.message);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to test provider');
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    try {
      await axios.delete(`${API_URL}/api/super-admin/providers/${providerId}`, { headers: getAuthHeader() });
      toast.success('Provider deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete provider');
    }
  };

  const toggleSecret = (id) => {
    setShowSecrets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getSelectedProviderFields = () => {
    const providerList = newProvider.provider_type === 'email' 
      ? availableProviders.email_providers 
      : availableProviders.whatsapp_providers;
    return providerList.find(p => p.name === newProvider.provider_name);
  };

  const emailProviders = providers.filter(p => p.provider_type === 'email');
  const whatsappProviders = providers.filter(p => p.provider_type === 'whatsapp');

  if (loading) {
    return (
      <DashboardLayout title="Provider Configuration">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Provider Configuration">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Email & WhatsApp Providers</h1>
            <p className="text-muted-foreground">Configure notification providers for the platform</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-provider-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Provider
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Configure New Provider</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Provider Type</Label>
                  <Select 
                    value={newProvider.provider_type} 
                    onValueChange={(v) => setNewProvider({...newProvider, provider_type: v, provider_name: '', config: {}})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">📧 Email Provider</SelectItem>
                      <SelectItem value="whatsapp">💬 WhatsApp Provider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select 
                    value={newProvider.provider_name} 
                    onValueChange={(v) => setNewProvider({...newProvider, provider_name: v, config: {}})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                    <SelectContent>
                      {(newProvider.provider_type === 'email' ? availableProviders.email_providers : availableProviders.whatsapp_providers).map(p => (
                        <SelectItem key={p.name} value={p.name}>{p.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Dynamic Fields */}
                {getSelectedProviderFields() && (
                  <>
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-3">Configuration</p>
                      <div className="space-y-3">
                        {getSelectedProviderFields().required_fields.map(field => (
                          <div key={field} className="space-y-1">
                            <Label className="text-sm capitalize">{field.replace(/_/g, ' ')} *</Label>
                            <Input 
                              type={field.includes('key') || field.includes('secret') || field.includes('token') || field.includes('password') ? 'password' : 'text'}
                              value={newProvider.config[field] || ''} 
                              onChange={(e) => setNewProvider({
                                ...newProvider, 
                                config: {...newProvider.config, [field]: e.target.value}
                              })} 
                              placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                            />
                          </div>
                        ))}
                        {getSelectedProviderFields().optional_fields.map(field => (
                          <div key={field} className="space-y-1">
                            <Label className="text-sm capitalize text-muted-foreground">{field.replace(/_/g, ' ')}</Label>
                            <Input 
                              value={newProvider.config[field] || ''} 
                              onChange={(e) => setNewProvider({
                                ...newProvider, 
                                config: {...newProvider.config, [field]: e.target.value}
                              })} 
                              placeholder={`Enter ${field.replace(/_/g, ' ')} (optional)`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={newProvider.is_default} 
                        onCheckedChange={(v) => setNewProvider({...newProvider, is_default: v})}
                      />
                      <Label>Set as default provider</Label>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateProvider} disabled={!newProvider.provider_name}>
                  Save Provider
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Alert */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">Provider Configuration</p>
              <p className="text-blue-700">
                Configure email and WhatsApp providers to enable notifications across the platform. 
                Schools can use these configured providers for sending notifications.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="payment" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Payment Gateway
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" /> Email ({emailProviders.length})
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" /> WhatsApp ({whatsappProviders.length})
            </TabsTrigger>
          </TabsList>

          {/* Payment Gateway Tab */}
          <TabsContent value="payment" className="mt-4">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-blue-600" />
                        Razorpay Payment Gateway
                      </CardTitle>
                      <CardDescription>
                        Configure payment gateway for school subscription payments
                      </CardDescription>
                    </div>
                    {gatewaySettings?.is_configured ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" /> Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600">
                        <AlertCircle className="h-3 w-3 mr-1" /> Not Configured
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {gatewaySettings?.is_configured ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                        <div>
                          <p className="text-sm text-muted-foreground">API Key ID</p>
                          <p className="font-mono text-sm">{gatewaySettings.key_id}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Merchant Name</p>
                          <p className="font-medium">{gatewaySettings.merchant_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Status</p>
                          <Badge variant={gatewaySettings.status === 'active' ? 'default' : 'secondary'}>
                            {gatewaySettings.status}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Source</p>
                          <p className="text-sm capitalize">{gatewaySettings.source}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={testGateway} disabled={gatewayLoading}>
                          {gatewayLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
                          Test Connection
                        </Button>
                        <Dialog open={gatewayDialogOpen} onOpenChange={setGatewayDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline">
                              <Settings className="h-4 w-4 mr-2" /> Update Keys
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Update Razorpay API Keys</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>API Key ID *</Label>
                                <Input
                                  value={gatewayForm.key_id}
                                  onChange={(e) => setGatewayForm({...gatewayForm, key_id: e.target.value})}
                                  placeholder="rzp_live_xxxxxxxxxx"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>API Key Secret *</Label>
                                <div className="relative">
                                  <Input
                                    type={showGatewaySecret ? 'text' : 'password'}
                                    value={gatewayForm.key_secret}
                                    onChange={(e) => setGatewayForm({...gatewayForm, key_secret: e.target.value})}
                                    placeholder="Enter secret key"
                                  />
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm" 
                                    className="absolute right-1 top-1 h-7"
                                    onClick={() => setShowGatewaySecret(!showGatewaySecret)}
                                  >
                                    {showGatewaySecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Merchant Name</Label>
                                <Input
                                  value={gatewayForm.merchant_name}
                                  onChange={(e) => setGatewayForm({...gatewayForm, merchant_name: e.target.value})}
                                  placeholder="Cloud Campus"
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setGatewayDialogOpen(false)}>Cancel</Button>
                              <Button onClick={saveGatewaySettings} disabled={gatewayLoading}>
                                {gatewayLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                Save
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Button variant="destructive" size="sm" onClick={removeGateway}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCard className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                      <h3 className="font-semibold mb-2">Payment Gateway Not Configured</h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        Schools won't be able to pay subscriptions online until you configure a payment gateway.
                      </p>
                      <Dialog open={gatewayDialogOpen} onOpenChange={setGatewayDialogOpen}>
                        <DialogTrigger asChild>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" /> Configure Razorpay
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Configure Razorpay Payment Gateway</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
                              <p className="font-medium">How to get Razorpay API Keys:</p>
                              <ol className="list-decimal list-inside mt-2 space-y-1">
                                <li>Go to <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="underline">dashboard.razorpay.com</a></li>
                                <li>Login to your account</li>
                                <li>Go to Settings → API Keys</li>
                                <li>Generate/Copy your Key ID and Secret</li>
                              </ol>
                            </div>
                            <div className="space-y-2">
                              <Label>API Key ID *</Label>
                              <Input
                                value={gatewayForm.key_id}
                                onChange={(e) => setGatewayForm({...gatewayForm, key_id: e.target.value})}
                                placeholder="rzp_live_xxxxxxxxxx"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>API Key Secret *</Label>
                              <div className="relative">
                                <Input
                                  type={showGatewaySecret ? 'text' : 'password'}
                                  value={gatewayForm.key_secret}
                                  onChange={(e) => setGatewayForm({...gatewayForm, key_secret: e.target.value})}
                                  placeholder="Enter secret key"
                                />
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="sm" 
                                  className="absolute right-1 top-1 h-7"
                                  onClick={() => setShowGatewaySecret(!showGatewaySecret)}
                                >
                                  {showGatewaySecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Merchant Name</Label>
                              <Input
                                value={gatewayForm.merchant_name}
                                onChange={(e) => setGatewayForm({...gatewayForm, merchant_name: e.target.value})}
                                placeholder="Cloud Campus"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setGatewayDialogOpen(false)}>Cancel</Button>
                            <Button onClick={saveGatewaySettings} disabled={gatewayLoading}>
                              {gatewayLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                              Configure Gateway
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info about Payment Flow */}
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-900">Payment Flow</p>
                      <ul className="text-amber-700 mt-1 space-y-1 list-disc list-inside">
                        <li>Schools can pay subscription fees online using this gateway</li>
                        <li>All online payments will be credited to your Razorpay account</li>
                        <li>You can also record offline payments manually in the Invoices section</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="email" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {emailProviders.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Mail className="h-12 w-12 text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground mb-4">No email providers configured</p>
                    <Button variant="outline" onClick={() => { setNewProvider({...newProvider, provider_type: 'email'}); setDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add Email Provider
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                emailProviders.map(provider => (
                  <ProviderCard 
                    key={provider.id} 
                    provider={provider} 
                    onTest={handleTestProvider}
                    onDelete={handleDeleteProvider}
                    showSecrets={showSecrets}
                    toggleSecret={toggleSecret}
                  />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {whatsappProviders.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <MessageCircle className="h-12 w-12 text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground mb-4">No WhatsApp providers configured</p>
                    <Button variant="outline" onClick={() => { setNewProvider({...newProvider, provider_type: 'whatsapp'}); setDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add WhatsApp Provider
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                whatsappProviders.map(provider => (
                  <ProviderCard 
                    key={provider.id} 
                    provider={provider} 
                    onTest={handleTestProvider}
                    onDelete={handleDeleteProvider}
                    showSecrets={showSecrets}
                    toggleSecret={toggleSecret}
                  />
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Available Providers Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Available Providers</CardTitle>
            <CardDescription>Supported providers you can configure</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email Providers
                </h4>
                <div className="space-y-2">
                  {availableProviders.email_providers.map(p => (
                    <div key={p.name} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span>{p.display_name}</span>
                      <Badge variant="outline" className="text-xs">{p.required_fields.length} fields</Badge>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" /> WhatsApp Providers
                </h4>
                <div className="space-y-2">
                  {availableProviders.whatsapp_providers.map(p => (
                    <div key={p.name} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span>{p.display_name}</span>
                      <Badge variant="outline" className="text-xs">{p.required_fields.length} fields</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const ProviderCard = ({ provider, onTest, onDelete, showSecrets, toggleSecret }) => {
  const getStatusIcon = () => {
    if (provider.test_status === 'success') return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (provider.test_status === 'failed') return <XCircle className="h-4 w-4 text-red-600" />;
    return <AlertCircle className="h-4 w-4 text-gray-400" />;
  };

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {provider.provider_type === 'email' ? <Mail className="h-5 w-5 text-blue-600" /> : <MessageCircle className="h-5 w-5 text-green-600" />}
              <CardTitle className="text-lg capitalize">{provider.provider_name}</CardTitle>
            </div>
            {getStatusIcon()}
          </div>
          <div className="flex items-center gap-2 mt-2">
            {provider.is_default && <Badge>Default</Badge>}
            {provider.is_active ? (
              <Badge variant="outline" className="text-green-600">Active</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400">Inactive</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {provider.config && Object.keys(provider.config).length > 0 && (
            <div className="space-y-1 text-sm mb-4">
              {Object.entries(provider.config).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-xs">
                      {showSecrets[provider.id] ? value : '••••••••'}
                    </span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => toggleSecret(provider.id)}>
                      {showSecrets[provider.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {provider.last_tested && (
            <p className="text-xs text-muted-foreground mb-4">
              Last tested: {new Date(provider.last_tested).toLocaleString()}
            </p>
          )}
          
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onTest(provider.id)}>
              <TestTube className="h-3 w-3 mr-1" /> Test
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onDelete(provider.id)}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ProvidersPage;
